import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Key,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type ThHTMLAttributes,
} from 'react'
import {
  App,
  Button,
  Checkbox,
  Flex,
  Input,
  Popover,
  Segmented,
  Space,
  Table,
  Tooltip,
  Typography,
  theme,
} from 'antd'
import {
  ColumnHeightOutlined,
  CompressOutlined,
  DownloadOutlined,
  ExpandOutlined,
  HolderOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import type { TableProps } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { downloadCsv, type CsvColumn } from '@/shared/lib/download'
import { EmptyState, type EmptyVariant } from '@/shared/ui/EmptyState'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import {
  PAGE_SIZE_OPTIONS,
  mergeColumnFilters,
  toQueryParams,
  type SortOrder,
  type TableState,
} from './tableState'
import { TableSkeleton } from './TableSkeleton'
import { renderSummary, type SummaryLine } from './tableSummary'
import { useTableUrlState } from './useTableUrlState'

/** Serverdan keladigan sahifa. Backend AYNAN shu shaklda qaytarishi kutiladi. */
export interface PageResult<T> {
  items: readonly T[]
  total: number
}

/**
 * Ommaviy amal — bir nechta belgilangan qatorga birdaniga qo'llanadi.
 *
 * ⚠️ TASDIQLASH SHU YERDA MAJBURIY va DataTable o'zi ko'rsatadi: chaqiruvchi
 * uni unutib qo'ymasin uchun `onConfirm` hech qachon to'g'ridan-to'g'ri
 * bosishda emas, faqat `Modal.confirm` orqali chaqiriladi — va matnda
 * NECHTA qatorga ta'sir qilishi aniq ko'rsatiladi (`confirmContent(count)`).
 */
export interface BulkAction<T> {
  key: string
  label: string
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  confirmTitle: (count: number) => string
  confirmContent: (count: number) => ReactNode
  onConfirm: (keys: readonly Key[], rows: readonly T[]) => Promise<void> | void
}

export interface DataTableProps<T> {
  /** Kesh kaliti asosi — jadval holati unga avtomatik qo'shiladi */
  queryKey: readonly unknown[]
  /**
   * Sahifani serverdan olib keladi.
   *
   * ⚠️ SAHIFALASH, SARALASH VA FILTRLASH — SERVER TOMONDA (07-FRONTEND.md §9).
   * `radacct` katta o'sadi, hammasini mijozga yuborib bo'lmaydi. Shuning uchun
   * bu funksiya doim `state` ni serverga uzatishi va faqat bir sahifa
   * qaytarishi kerak.
   */
  fetchPage: (state: TableState, signal: AbortSignal) => Promise<PageResult<T>>
  columns: ColumnsType<T>
  rowKey: (row: T) => string

  /** Bitta sahifada ikkinchi jadval bo'lsa — URL parametr prefiksi */
  urlPrefix?: string
  defaultPageSize?: number

  /** Qidiruv maydoni ko'rsatilsinmi (server tomonda qidiradi) */
  searchable?: boolean
  searchPlaceholder?: string

  /**
   * 10 000+ qator uchun virtual scroll. Yoqilganda `scrollY` majburiy —
   * antd virtual rejimda balandlikni o'zi hisoblay olmaydi.
   */
  virtual?: boolean
  scrollY?: number
  /** Gorizontal scroll uchun eng kichik kenglik (tor ekranda jadval siqilmasin) */
  scrollX?: number

  /** Avtomatik yangilash oralig'i, ms. Faol sessiyalar uchun 10–30 s. */
  refetchInterval?: number

  /** Sarlavha qatoriga qo'shimcha tugmalar ("Yangi abonent" va h.k.) */
  toolbarExtra?: ReactNode
  size?: TableProps<T>['size']
  onRow?: TableProps<T>['onRow']
  expandable?: TableProps<T>['expandable']

  /**
   * Ustunlar orasida TIK CHIZIQ.
   *
   * ⚠️ Sukut bo'yicha O'CHIQ — mavjud jadvallarning ko'rinishi
   * o'zgarmasin. Yoqilishi kerak bo'lgan yagona holat o'lchangan
   * (tester-2, BUG-015, `VenueCampaignsPage`): qo'shni kataklar
   * chegarasi `0px` va matnlari orasida atigi 16px chekinish qolganda
   * uzun qiymatlar bir qatorga qo'shilib ko'rinadi.
   */
  bordered?: boolean

  /**
   * "1–25 / 1 240" yozuvi ko'rsatilsinmi.
   *
   * ⚠️ Hamma endpoint ham JAMI SONNI BILMAYDI: `SessionPage` faqat `hasNext`
   * beradi (`SubscriberPage` esa `total`). Jami son noma'lum bo'lganda uni
   * baribir chizish — taxminni haqiqat qilib ko'rsatish. Shuning uchun
   * bunday jadvallar buni `false` qiladi va faqat sahifa tugmalari qoladi.
   */
  showTotal?: boolean

  /**
   * Barqaror id — ustunlarni ko'rsatish/yashirish/tartiblash va ustun
   * kengligi shu kalit ostida `localStorage`da saqlanadi. Berilmasa bu
   * imkoniyatlar chizilmaydi (ko'p jadvalli sahifada nomuvofiq holat
   * saqlanib qolmasin).
   */
  tableId?: string
  /** Birinchi ustun gorizontal scrollda joyida qolsin (login, ID va h.k.) */
  stickyFirstColumn?: boolean

  /**
   * Tartib raqami ustuni. Sukut bo'yicha YOQIQ — operator qatorni
   * "uchinchisi" deb ko'rsata olishi kerak. Raqam sahifa bo'yicha
   * uzluksiz: 2-sahifada 11 dan boshlanadi.
   */
  showRowNumber?: boolean

  /** CSV eksport ustunlari — berilsa yuklab olish tugmasi paydo bo'ladi */
  exportColumns?: readonly CsvColumn<T>[]
  exportFilename?: string
  /**
   * Eksport izohiga qo'shiladigan matn.
   *
   * ⚠️ Nega kerak bo'ldi: bu jadvaldagi eksport BRAUZERDA yasaladi va
   * faqat joriy sahifani oladi — bu barcha jadvallar uchun to'g'ri.
   * Lekin ayrim ro'yxatlarning SERVERDAGI to'liq eksporti ham bor
   * (`shared/lib/serverExport.ts`), va operatorga u qayerdaligini
   * aytmaslik "to'liq eksport yo'q ekan" degan xulosaga olib borardi.
   */
  exportNoteExtra?: ReactNode

  /** Qatorlarni belgilash orqali ommaviy amallar */
  bulkActions?: readonly BulkAction<T>[]
  /** Qaysi qatorlar tanlanishi mumkin — bo'lmasa hammasi tanlanadi */
  isRowSelectable?: (row: T) => boolean

  /** Bo'sh holat sozlamalari — standart matn o'rniga aniqroq tushuntirish */
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: { label: string; onClick: () => void }
  /**
   * Bo'sh holat illyustratsiyasi. Standart `no-data` — jadvalda
   * qator yo'qligi eng ko'p uchraydigan holat.
   */
  emptyVariant?: EmptyVariant
  /** Joriy sahifa qatorlari ostidagi jami — faqat SHU sahifa bo'yicha, yorliq buni aytishi shart */
  summary?: (rows: readonly T[]) => readonly SummaryLine[]
}

const SEARCH_DEBOUNCE_MS = 350
const DENSITY_KEY = 'xsib.table.density'
const DENSITY_OPTIONS = ['small', 'middle', 'large'] as const
type Density = (typeof DENSITY_OPTIONS)[number]

function isDensity(value: string | null): value is Density {
  return value !== null && (DENSITY_OPTIONS as readonly string[]).includes(value)
}

function readDensity(fallback: Density): Density {
  try {
    const stored = localStorage.getItem(DENSITY_KEY)
    return isDensity(stored) ? stored : fallback
  } catch {
    return fallback
  }
}

function columnKey<T>(column: ColumnsType<T>[number], index: number): string {
  const withKey = column as ColumnType<T>
  return String(withKey.key ?? withKey.dataIndex ?? index)
}

function columnLabel<T>(column: ColumnsType<T>[number], index: number): string {
  const withKey = column as ColumnType<T>
  return typeof withKey.title === 'string' ? withKey.title : columnKey(column, index)
}

function readStringList(key: string | null): string[] {
  if (!key) return []
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function readWidthMap(key: string | null): Record<string, number> {
  if (!key) return {}
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '{}')
    if (parsed && typeof parsed === 'object') return parsed as Record<string, number>
    return {}
  } catch {
    return {}
  }
}

interface ResizableCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /**
   * antd bu maydonni ham `onHeaderCell` natijasidan quyi cell'ga uzatadi —
   * `<th>` o'zi tushunmaydi, shuning uchun spread qilishdan OLDIN ajratib
   * olinishi shart (aks holda DOM "Unknown prop" ogohlantirishi beradi).
   */
  width?: number
  onResizeStart?: (e: ReactMouseEvent<HTMLSpanElement>) => void
}

/**
 * Sichqoncha bilan kengaytiriladigan ustun sarlavhasi.
 *
 * NEGA TASHQI KUTUBXONASIZ: `react-resizable` qo'shish bitta funksiya uchun
 * yangi bog'liqlik degani. O'ng chetdagi tor chiziq — drag tutqichi — oddiy
 * `mousemove`/`mouseup` bilan ham yetarli va kod ancha kichik qoladi.
 */
function ResizableHeaderCell({ width: _width, onResizeStart, children, ...rest }: ResizableCellProps) {
  if (!onResizeStart) {
    return <th {...rest}>{children}</th>
  }
  return (
    <th {...rest} style={{ ...rest.style, position: 'relative', userSelect: 'none' }}>
      {children}
      <span
        onMouseDown={onResizeStart}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          right: -3,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          zIndex: 1,
          touchAction: 'none',
        }}
      />
    </th>
  )
}

/**
 * Panelning eng ko'p ishlatiladigan komponenti (07-FRONTEND.md §9).
 *
 * Bir joyda birlashtiradi:
 *   · server tomonda sahifalash / saralash / filtr / qidiruv
 *   · holat URL'da — yangilanganda yo'qolmaydi, havola ulashiladi
 *   · `keepPreviousData` — sahifa almashganda jadval bo'shab sakramaydi
 *   · 10 000+ qator uchun virtual rejim
 *   · ustunlarni ko'rsatish/yashirish/tartiblash/kenglik — `localStorage`da
 *   · zichlik (compact/o'rtacha/keng) — GLOBAL sozlama, hamma jadvalda bir xil
 *   · CSV eksport (JORIY SAHIFA — server to'liq eksport bermaydi, shuning
 *     uchun tugma yonida bu ANIQ aytiladi, yolg'on va'da yo'q)
 *   · ommaviy amallar — tasdiqlash modali bilan MAJBURIY
 *   · bo'sh holat va skeleton yuklanish — spinner emas, shakl
 *
 * Har feature buni ishlatadi va o'zi jadval yozmaydi. Sabab oddiy: bu
 * narsalardan bittasi unutilsa, u faqat prod'da, katta ma'lumotda ko'rinadi.
 */
export function DataTable<T>({
  queryKey,
  fetchPage,
  columns,
  rowKey,
  urlPrefix = '',
  defaultPageSize,
  searchable = false,
  searchPlaceholder,
  virtual = false,
  scrollY,
  scrollX,
  refetchInterval,
  toolbarExtra,
  size = 'middle',
  onRow,
  expandable,
  bordered = false,
  showTotal = true,
  tableId,
  stickyFirstColumn = false,
  showRowNumber = true,
  exportColumns,
  exportFilename,
  exportNoteExtra,
  bulkActions,
  isRowSelectable,
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyVariant = 'no-data',
  summary,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const { state, patch, reset } = useTableUrlState({
    prefix: urlPrefix,
    ...(defaultPageSize === undefined ? {} : { defaultPageSize }),
  })

  const query = useQuery<PageResult<T>>({
    queryKey: [...queryKey, toQueryParams(state)],
    queryFn: ({ signal }) => fetchPage(state, signal),
    // Sahifa almashganda eskisi ekranda qoladi va faqat mazmuni yangilanadi.
    // Busiz jadval bir zumga bo'shab, balandligi o'zgarardi — sichqoncha
    // ko'rsatkichi ostidagi qator siljib ketardi.
    placeholderData: keepPreviousData,
    ...(refetchInterval === undefined ? {} : { refetchInterval }),
  })

  // Qidiruv maydoni mahalliy holatda: har harfda URL yozish va so'rov
  // yuborish shovqin bo'lardi. URL'ga faqat to'xtagandan keyin yoziladi.
  const [searchText, setSearchText] = useState(state.search)
  useEffect(() => setSearchText(state.search), [state.search])
  useEffect(() => {
    if (searchText === state.search) return
    const timer = setTimeout(() => patch({ search: searchText }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchText, state.search, patch])

  // --- To'liq ekran — jadval ko'p ustunli bo'lsa butun oynani egallaydi.
  //     Sahifa holati (URL/localStorage) emas: bir martalik ko'rish rejimi.
  const [fullscreen, setFullscreen] = useState(false)
  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setFullscreen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullscreen])

  // --- Zichlik — GLOBAL sozlama, `size` prop faqat BOSHLANG'ICH qiymat ---
  const [density, setDensity] = useState<Density>(() => readDensity(size))
  const changeDensity = (next: Density) => {
    setDensity(next)
    try {
      localStorage.setItem(DENSITY_KEY, next)
    } catch {
      // localStorage yopiq bo'lsa ham jadval ishlayveradi, faqat esda qolmaydi
    }
  }

  // --- Ustunlarni ko'rsatish/yashirish va tartib — `tableId` bo'lsa saqlanadi ---
  const hiddenKey = tableId ? `xsib.table.cols.${tableId}` : null
  const orderKey = tableId ? `xsib.table.colorder.${tableId}` : null
  const widthKey = tableId ? `xsib.table.colwidth.${tableId}` : null
  const [hidden, setHidden] = useState<string[]>(() => readStringList(hiddenKey))
  const [order, setOrder] = useState<string[]>(() => readStringList(orderKey))
  const [widths, setWidths] = useState<Record<string, number>>(() => readWidthMap(widthKey))

  const toggleColumn = (key: string, visible: boolean) => {
    setHidden((prev) => {
      const next = visible ? prev.filter((k) => k !== key) : [...prev, key]
      if (hiddenKey) {
        try {
          localStorage.setItem(hiddenKey, JSON.stringify(next))
        } catch {
          // e'tiborsiz
        }
      }
      return next
    })
  }

  const orderedKeys = useMemo(() => {
    const allKeys = columns.map((c, i) => columnKey(c, i))
    return [...order.filter((k) => allKeys.includes(k)), ...allKeys.filter((k) => !order.includes(k))]
  }, [columns, order])

  const byKey = useMemo(() => {
    const map = new Map<string, ColumnsType<T>[number]>()
    columns.forEach((col, idx) => map.set(columnKey(col, idx), col))
    return map
  }, [columns])

  const dragKeyRef = useRef<string | null>(null)
  const reorderColumn = (fromKey: string, toKey: string) => {
    if (fromKey === toKey) return
    const list = [...orderedKeys]
    const from = list.indexOf(fromKey)
    const to = list.indexOf(toKey)
    if (from < 0 || to < 0) return
    const [moved] = list.splice(from, 1)
    if (moved === undefined) return
    list.splice(to, 0, moved)
    setOrder(list)
    if (orderKey) {
      try {
        localStorage.setItem(orderKey, JSON.stringify(list))
      } catch {
        // e'tiborsiz
      }
    }
  }

  const persistWidths = (next: Record<string, number>) => {
    if (!widthKey) return
    try {
      localStorage.setItem(widthKey, JSON.stringify(next))
    } catch {
      // e'tiborsiz
    }
  }

  /** Sichqoncha bilan tortishni boshlaydi — `mouseup`gacha davom etadi */
  const startResize = (key: string, startWidth: number) => (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault()
    const startX = e.clientX
    let latest = startWidth
    const onMove = (moveEvent: MouseEvent) => {
      latest = Math.max(60, startWidth + (moveEvent.clientX - startX))
      setWidths((prev) => ({ ...prev, [key]: latest }))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      setWidths((prev) => {
        const next = { ...prev, [key]: latest }
        persistWidths(next)
        return next
      })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // antd ustunlarga joriy saralash/filtr holatini QAYTA BERAMIZ: manba
  // haqiqat — URL, jadvalning ichki holati emas. Busiz sahifa yangilangach
  // URL'da filtr turadi, lekin ustun sarlavhasi uni ko'rsatmaydi.
  //
  // SHU YERDA ham: ko'rinish tartibi, yashirilgan ustunlar va (agar
  // `tableId` bo'lsa) sichqoncha bilan o'zgartirilgan kenglik qo'llanadi.
  const visibleColumns = useMemo<ColumnsType<T>>(() => {
    const base = tableId
      ? orderedKeys
          .filter((k) => !hidden.includes(k))
          .map((k) => byKey.get(k))
          .filter((c): c is ColumnsType<T>[number] => c !== undefined)
      : columns

    return base.map((column, index) => {
      const key = columnKey(column, index)
      const filtered = state.filters[key]
      const withState: ColumnsType<T>[number] = {
        ...column,
        sortOrder: state.sortField === key ? (state.sortOrder ?? null) : null,
        filteredValue: filtered && filtered.length > 0 ? [...filtered] : null,
      }
      if (!tableId) return withState

      const original = withState as ColumnType<T>
      const startWidth = widths[key] ?? (typeof original.width === 'number' ? original.width : 150)
      return {
        ...withState,
        width: startWidth,
        onHeaderCell: () => ({
          width: startWidth,
          onResizeStart: startResize(key, startWidth),
        }),
      }
    })
    // ⚠️ `startResize` ATAYLAB yo'q: u har chizishda qayta yaratiladigan
    //    funksiya va ro'yxatga qo'shilsa `useMemo` har render'da qayta
    //    hisoblanardi — ya'ni 20+ sahifadagi jadval ustunlari sababsiz
    //    qayta qurilardi. Eskirmaydi: ichida faqat DOM hodisasi va holat
    //    o'rnatuvchisi bor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, orderedKeys, hidden, byKey, columns, state.filters, state.sortField, state.sortOrder, widths])

  // Birinchi (ko'rinadigan) ustun scroll paytida joyida qolsin — login
  // ustuni gorizontal scrollda ham ko'rinib tursin
  /**
   * Tartib raqami — SAHIFA bo'yicha uzluksiz: 2-sahifada 11, 12, …
   *
   * ⚠️ Raqam `dataIndex` emas, hisoblanadi: server qatorlarida bunday
   * maydon yo'q va uni har sahifada qo'shish 50 ta chaqiruv joyini
   * o'zgartirishni talab qilardi. Bu yerda esa manba bitta.
   *
   * ⚠️ Ustun ro'yxatiga `visibleColumns` DAN KEYIN qo'shiladi: u
   * `tableId` sozlamalariga (ko'rsatish/tartib/kenglik) kirmaydi —
   * aks holda operator uni yashirib qo'yib, keyin qaytara olmasdi.
   */
  const stickyColumns = useMemo<ColumnsType<T>>(() => {
    // ⚠️ Yopishqoqlik AVVAL asl birinchi ustunga qo'llanadi, raqam esa
    // KEYIN qo'shiladi: teskarisida `stickyFirstColumn` raqamga tushib,
    // login/ID ustuni scrollda oqib ketardi.
    const base =
      stickyFirstColumn && visibleColumns.length > 0
        ? visibleColumns.map((column, index) => (index === 0 ? { ...column, fixed: 'left' as const } : column))
        : visibleColumns
    if (!showRowNumber) return base

    const offset = (state.page - 1) * state.pageSize
    const indexColumn: ColumnsType<T>[number] = {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      ...(stickyFirstColumn ? { fixed: 'left' as const } : {}),
      render: (_value: unknown, _row: T, index: number) => (
        <Typography.Text type="secondary">{offset + index + 1}</Typography.Text>
      ),
    }
    return [indexColumn, ...base]
  }, [showRowNumber, visibleColumns, state.page, state.pageSize, stickyFirstColumn, t])

  /**
   * Jadvalning HAQIQIY eng kichik kengligi — X-SIB-011 sinfidagi nuqson.
   *
   * <h2>Nima buzilgan edi</h2>
   * `scroll.x` antd uchun "jadval shuncha keng" degani, ustun kengliklari
   * esa `<colgroup>` ga tushadi va `table-layout: fixed` bilan ishlaydi.
   * Agar ustunlar yig'indisi `scroll.x` dan KATTA bo'lsa, brauzer ularni
   * sig'dirish uchun PROPORSIONAL SIQADI — sarlavhalar va qiymatlar
   * bir-biriga yopishadi. Gorizontal scroll esa paydo BO'LMAYDI, chunki
   * jadval o'z konteyneriga "sig'gan".
   *
   * Bu chaqiruvchining xatosiga o'xshaydi, lekin amalda TUZOQ:
   *
   * 1. `tableId` berilgan jadvalda HAR ustunga majburiy kenglik qo'yiladi
   *    (yuqoridagi `startWidth`, kenglik berilmagan ustunga 150) — ya'ni
   *    yig'indi chaqiruvchi hisoblaganidan katta bo'lib chiqadi.
   *    `SitesPage` da aynan shu: yig'indi 1130, `scrollX` esa 1100.
   * 2. Ustunni sichqoncha bilan kengaytirish `widths` ni oshiradi, lekin
   *    `scrollX` o'zgarmaydi. Ya'ni operator bitta ustunni kengaytirsa,
   *    QOLGAN HAMMASI siqilardi — imkoniyat o'z-o'ziga qarshi ishlardi.
   *
   * Shuning uchun `scrollX` endi CHEKLOV emas, POL: kerak bo'lganda
   * jadval undan kengroq bo'ladi va gorizontal scroll paydo bo'ladi —
   * bu to'g'ri xatti-harakat, siqilish esa hech qachon to'g'ri emas.
   */
  const minTableWidth = useMemo(() => {
    const sum = stickyColumns.reduce<number>((total, column) => {
      const width = (column as ColumnType<T>).width
      return total + (typeof width === 'number' ? width : 0)
    }, 0)
    // Tanlash katakchasi ustuni ham joy oladi — antd uni 32 px atrofida chizadi
    const selectionWidth = bulkActions && bulkActions.length > 0 ? 48 : 0
    return sum + selectionWidth
  }, [stickyColumns, bulkActions])

  /*
    ⚠️ `scrollX` BERILMAGAN holat TEGILMAYDI — eskisicha `max-content`.

    U yerda siqilish bo'lishi mumkin emas: `max-content` brauzerga
    "mazmunga qancha kerak bo'lsa shuncha" deydi, ya'ni yig'indi
    allaqachon hisobga olingan. Uni son bilan almashtirish o'nlab
    jadvalning kengligini sababsiz o'zgartirardi — tuzatishga aloqasi
    yo'q regressiya xavfi.
  */
  const effectiveScrollX: number | 'max-content' =
    scrollX === undefined ? 'max-content' : Math.max(scrollX, minTableWidth)

  const handleChange: NonNullable<TableProps<T>['onChange']> = (pagination, filters, sorter) => {
    const single: SorterResult<T> | undefined = Array.isArray(sorter) ? sorter[0] : sorter
    const sortField = single?.order && single.columnKey !== undefined ? String(single.columnKey) : null
    const sortOrder: SortOrder | null = single?.order ?? null

    /*
      ⚠️ MAVJUD FILTRLAR USTIGA QO'YILADI, NOLDAN QURILMAYDI.

      O'lchangan nuqson (2026-08-21): bu yerda `nextFilters` bo'sh
      obyektdan boshlanardi va antd bergan ro'yxat bilan to'ldirilardi.
      antd esa FAQAT `filters` (yoki `filterDropdown`/`onFilter`) e'lon
      qilgan ustunlarni biladi (`useFilter/collectFilterStates`) — ya'ni
      sarlavha panelidagi yoki filtr panelidagi tanlov, agar u ustunda
      e'lon qilinmagan bo'lsa, HAR sahifa almashtirilishida va HAR
      saralashda JIMGINA o'chib ketardi.

      Bu eng yomon turdagi nuqson: ekran ishlayotgandek ko'rinadi,
      ro'yxat esa boshqa narsani ko'rsatadi. O'lchangan ta'sir —
      abonentlar (`phone`, `agent`), lidlar (`source`), murojaatlar
      (`category`, `priority`, `overdue`), muloqotlar, jurnal,
      berilgan paketlar, vedomostlar.

      ⚠️ ANTD BERGAN QIYMAT USTUN TURADI — BO'SH BO'LSA HAM. Aks holda
      teskari nuqson chiqardi: ustun filtrini ATAYLAB tozalash
      ishlamay qolardi, chunki eski qiymat har safar qayta tiklanardi.
      Shuning uchun kalit UMUMAN kelmagan (e'lon qilinmagan) holat bilan
      `null`/bo'sh kelgan (tozalangan) holat ATAYLAB ajratilgan.

      «Hammasini tozalash» yo'li o'zgarmadi — u `reset()` da va u
      `filters: {}` ni to'g'ridan-to'g'ri yozadi.
    */
    const nextFilters = mergeColumnFilters(state.filters, filters)

    patch({
      page: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? state.pageSize,
      sortField,
      sortOrder,
      filters: nextFilters,
    })
  }

  // --- Ommaviy amallar: tanlov sahifa/filtr o'zgarganda TOZALANADI —
  // aks holda ko'rinmaydigan qatorni "tanlangan" deb qoldirish adashtiradi
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([])
  const filtersFingerprint = JSON.stringify(state.filters)
  useEffect(() => {
    setSelectedKeys([])
  }, [state.page, state.pageSize, state.search, state.sortField, state.sortOrder, filtersFingerprint])

  const rows = query.data ? [...query.data.items] : []
  const selectedRows = rows.filter((row) => selectedKeys.includes(rowKey(row)))

  const runBulkAction = (action: BulkAction<T>) => {
    const count = selectedKeys.length
    modal.confirm({
      title: action.confirmTitle(count),
      content: action.confirmContent(count),
      okText: action.label,
      cancelText: t('common.cancel'),
      okButtonProps: { danger: action.danger ?? false },
      onOk: async () => {
        await action.onConfirm(selectedKeys, selectedRows)
        setSelectedKeys([])
      },
    })
  }

  const handleExport = () => {
    if (!exportColumns || rows.length === 0) return
    const name = exportFilename ?? tableId ?? 'export'
    const stamp = new Date().toISOString().slice(0, 10)
    downloadCsv(`${name}-${stamp}.csv`, rows, exportColumns)
  }

  const hasActiveFilters = Object.keys(state.filters).length > 0 || state.search !== '' || state.sortField !== null

  // ⚠️ Xato holati UMUMIY komponentda: xuddi shu uch qism (qizil, SABAB,
  // qayta urinish) `DataTable` ga o'tmagan sahifalarda ham kerak va u
  // yerda takrorlanardi — takror esa bittasini unutishga olib boradi.
  if (query.isError) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  const showToolbar = true // qidiruv bo'lmasa ham zichlik/ustun/yangilash tugmalari doim bor

  return (
    <Flex
      vertical
      gap="middle"
      style={
        fullscreen
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              overflow: 'auto',
              padding: 16,
              background: token.colorBgLayout,
            }
          : {}
      }
    >
      {showToolbar && (
        <Flex align="center" justify="space-between" gap="small" wrap>
          <Space wrap>
            {searchable && (
              <Input.Search
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={searchPlaceholder ?? t('dataTable.searchPlaceholder')}
                style={{ width: 260, maxWidth: '100%' }}
              />
            )}
            {hasActiveFilters && (
              <Button type="link" size="small" onClick={reset}>
                {t('dataTable.resetFilters')}
              </Button>
            )}
          </Space>

          <Space wrap>
            {toolbarExtra}

            <Tooltip title={t('dataTable.refresh')}>
              <Button
                type="text"
                aria-label={t('dataTable.refresh')}
                icon={<ReloadOutlined spin={query.isFetching} />}
                onClick={() => void queryClient.invalidateQueries({ queryKey })}
              />
            </Tooltip>

            {exportColumns ? (
              <Tooltip title={t('dataTable.exportHint')}>
                <Button
                  type="text"
                  aria-label={t('dataTable.export')}
                  icon={<DownloadOutlined />}
                  disabled={rows.length === 0}
                  onClick={handleExport}
                />
              </Tooltip>
            ) : null}

            <Popover
              trigger="click"
              placement="bottomRight"
              title={t('dataTable.density')}
              content={
                <Segmented<Density>
                  value={density}
                  onChange={changeDensity}
                  options={[
                    { label: t('dataTable.densitySmall'), value: 'small' },
                    { label: t('dataTable.densityMiddle'), value: 'middle' },
                    { label: t('dataTable.densityLarge'), value: 'large' },
                  ]}
                />
              }
            >
              <Tooltip title={t('dataTable.density')}>
                <Button type="text" aria-label={t('dataTable.density')} icon={<ColumnHeightOutlined />} />
              </Tooltip>
            </Popover>

            {tableId ? (
              <Popover
                trigger="click"
                placement="bottomRight"
                title={t('dataTable.columns')}
                content={
                  <div style={{ maxHeight: 340, overflow: 'auto', minWidth: 230 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      {t('dataTable.columnsHint')}
                    </Typography.Text>
                    {orderedKeys.map((key) => {
                      const col = byKey.get(key)
                      if (!col) return null
                      const idx = columns.indexOf(col)
                      return (
                        <div
                          key={key}
                          draggable
                          onDragStart={() => {
                            dragKeyRef.current = key
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            if (dragKeyRef.current) reorderColumn(dragKeyRef.current, key)
                            dragKeyRef.current = null
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'grab' }}
                        >
                          <HolderOutlined style={{ color: token.colorTextTertiary }} />
                          <Checkbox checked={!hidden.includes(key)} onChange={(e) => toggleColumn(key, e.target.checked)}>
                            {columnLabel(col, idx)}
                          </Checkbox>
                        </div>
                      )
                    })}
                  </div>
                }
              >
                <Tooltip title={t('dataTable.columns')}>
                  <Button type="text" aria-label={t('dataTable.columns')} icon={<SettingOutlined />} />
                </Tooltip>
              </Popover>
            ) : null}

            <Tooltip title={fullscreen ? t('dataTable.exitFullscreen') : t('dataTable.fullscreen')}>
              <Button
                type="text"
                aria-label={fullscreen ? t('dataTable.exitFullscreen') : t('dataTable.fullscreen')}
                icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={() => setFullscreen((v) => !v)}
              />
            </Tooltip>
          </Space>
        </Flex>
      )}

      {bulkActions && bulkActions.length > 0 && selectedKeys.length > 0 ? (
        <Flex
          align="center"
          justify="space-between"
          gap="small"
          wrap
          style={{
            background: token.colorFillTertiary,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: '8px 12px',
          }}
        >
          <Typography.Text strong>{t('dataTable.selected', { count: selectedKeys.length })}</Typography.Text>
          <Space wrap>
            {bulkActions.map((action) => (
              <Button
                key={action.key}
                danger={action.danger}
                disabled={action.disabled}
                icon={action.icon}
                onClick={() => runBulkAction(action)}
              >
                {action.label}
              </Button>
            ))}
            <Button type="link" size="small" onClick={() => setSelectedKeys([])}>
              {t('dataTable.clearSelection')}
            </Button>
          </Space>
        </Flex>
      ) : null}

      {query.isPending ? (
        <TableSkeleton />
      ) : (
        <Table<T>
          columns={stickyColumns}
          dataSource={rows}
          rowKey={rowKey}
          loading={query.isFetching}
          size={density}
          bordered={bordered}
          virtual={virtual}
          sticky={{ offsetHeader: fullscreen ? 0 : 64 }}
          scroll={
            virtual
              ? { y: scrollY ?? 480, x: effectiveScrollX }
              : { x: effectiveScrollX }
          }
          onChange={handleChange}
          {...(tableId ? { components: { header: { cell: ResizableHeaderCell } } } : {})}
          {...(onRow ? { onRow } : {})}
          {...(expandable ? { expandable } : {})}
          {...(summary
            ? {
                summary: () =>
                  renderSummary(
                    stickyColumns,
                    summary(rows),
                    (bulkActions && bulkActions.length > 0 ? 1 : 0) + (expandable ? 1 : 0),
                  ),
              }
            : {})}
          {...(bulkActions && bulkActions.length > 0
            ? {
                rowSelection: {
                  selectedRowKeys: selectedKeys,
                  onChange: (keys: Key[]) => setSelectedKeys(keys),
                  preserveSelectedRowKeys: false,
                  ...(isRowSelectable
                    ? {
                        getCheckboxProps: (row: T) => ({ disabled: !isRowSelectable(row) }),
                      }
                    : {}),
                },
              }
            : {})}
          locale={{
            emptyText: hasActiveFilters ? (
              <EmptyState
                variant="no-data"
                title={t('dataTable.emptyFiltered')}
                description={t('dataTable.emptyFilteredHint')}
                actionLabel={t('dataTable.clearFilters')}
                onAction={reset}
              />
            ) : (
              <EmptyState
                variant={emptyVariant}
                title={emptyTitle ?? t('common.empty')}
                {...(emptyDescription === undefined ? {} : { description: emptyDescription })}
                {...(emptyAction
                  ? { actionLabel: emptyAction.label, onAction: emptyAction.onClick }
                  : {})}
              />
            ),
          }}
          pagination={{
            current: state.page,
            pageSize: state.pageSize,
            total: query.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            responsive: true,
            // Jami son ma'lum bo'lsa ko'rsatamiz: "1–25 / 12 480" operatorga
            // ro'yxat qanchaligini bir qarashda aytadi. Noma'lum bo'lganda
            // (`showTotal={false}`) umuman chizilmaydi — yuqoridagi izoh.
            ...(showTotal
              ? {
                  showTotal: (total: number, range: [number, number]) => (
                    <Typography.Text type="secondary">
                      {t('dataTable.rangeOfTotal', { from: range[0], to: range[1], total })}
                    </Typography.Text>
                  ),
                }
              : {}),
          }}
        />
      )}

      {exportColumns ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('dataTable.exportScopeNote')}
          {exportNoteExtra ? <> {exportNoteExtra}</> : null}
        </Typography.Text>
      ) : null}
    </Flex>
  )
}
